'use client';

import { ReactNode, useEffect, useMemo, useState } from 'react';

import Image from 'next/image';

// import Image from 'next/image';
import { getCalApi } from '@calcom/embed-react';
import { motion, type Easing } from 'framer-motion';
import { useTheme } from 'next-themes';

import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { cn } from '@kit/ui/utils';

import Background from './background';

const ease: Easing = [0.16, 1, 0.3, 1];

function HeroPill({ label, url }: { label: string; url: string }) {
  return (
    <motion.a
      href={url}
      // TODO: check ring-1 ring-accent => border
      className="relative z-30 flex w-auto max-w-1440 items-center space-x-2 rounded-[100px] p-0.5 pr-2.5 ring-1 ring-white/[12%]"
      style={{
        background:
          'linear-gradient(180deg, rgba(0, 0, 0, 0.10) 0%, rgba(0, 0, 0, 0.00) 100%), #1E1D26',
      }}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease }}
    >
      <div
        className="w-fit rounded-[100px] px-[9px] py-0.5 text-xs font-semibold uppercase leading-6 tracking-[1.008px] text-white ring-1 ring-white/[12%]"
        style={{
          background:
            'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.10) 100%), rgba(255, 255, 255, 0.10)',
        }}
      >
        NEWS
      </div>
      <p className="text-xs font-medium leading-6 tracking-[1.008px] text-white">
        {label}
      </p>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M6.66669 10.6667L8.62625 8.7071C9.01677 8.31658 9.01677 7.68341 8.62625 7.29289L6.66669 5.33333"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </motion.a>
  );
}

function HeroTitles({
  title = '',
  subTitle,
}: {
  title?: string;
  subTitle?: ReactNode;
}) {
  return (
    <div className="relative z-30 mt-2.5 flex w-full max-w-2xl flex-col space-y-4 overflow-hidden lg:mt-4">
      <motion.h1
        className="flex flex-wrap justify-center gap-3 gap-y-0 text-5xl font-medium tracking-[-0.48px] text-content-primary-inverted dark:text-content-primary lg:text-[56px] lg:tracking-[-0.56px]"
        initial={{ filter: 'blur(10px)', opacity: 0, y: 50 }}
        animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
        transition={{
          duration: 1,
          ease,
          staggerChildren: 0.2,
        }}
      >
        {title.split(/\s+/i).map((text, index) => (
          <motion.span
            key={index}
            className="inline-block text-balance font-semibold leading-[1.2] tracking-[0.1px] lg:leading-[60px] lg:tracking-normal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: index * 0.2,
              ease,
            }}
          >
            {text}
          </motion.span>
        ))}
      </motion.h1>
      <motion.p
        className="mx-auto !mt-6 max-w-[510px] text-center text-sm leading-5 text-content-secondary lg:text-balance lg:text-[20px] lg:leading-[1.4]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.6,
          duration: 0.8,
          ease,
        }}
      >
        {subTitle}
      </motion.p>
    </div>
  );
}

function HeroCTA({ label, url }: { label: string; url?: string }) {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    const fetchData = async function () {
      const cal = await getCalApi({
        namespace: process.env.NEXT_PUBLIC_DEMO_NAMESPACE,
      });
      cal('ui', {
        hideEventTypeDetails: false,
        layout: 'month_view',
        theme: resolvedTheme as 'light' | 'dark',
      });
    };
    void fetchData();
  }, [resolvedTheme]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const calConfig = useMemo(
    () => ({
      layout: 'month_view',
      theme: resolvedTheme as 'light' | 'dark',
    }),
    [resolvedTheme],
  );

  return (
    <motion.div
      className="relative z-30 mx-auto mt-8 h-11 max-w-1440 lg:mt-10"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8, duration: 0.8, ease }}
    >
      <If condition={mounted}>
        <a href={url}>
          <Button
            variant="default"
            className={
              'h-11 rounded-[62px] px-[18px] py-3 text-sm font-semibold leading-5 text-foreground dark:text-background'
            }
            data-cal-namespace={process.env.NEXT_PUBLIC_DEMO_NAMESPACE}
            data-cal-link={process.env.NEXT_PUBLIC_DEMO_CAL_LINK}
            data-cal-config={JSON.stringify(calConfig)}
            style={{
              background:
                'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.10) 123.75%), #FFF',
              boxShadow:
                '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px -1px rgba(0, 0, 0, 0.10)',
            }}
          >
            {label}
          </Button>
        </a>
      </If>
    </motion.div>
  );
}

function HeroImageDark({
  imageSource,
  imageSourceMobile,
  className = '',
}: {
  imageSource: string;
  imageSourceMobile: string;
  className?: string;
}) {
  return (
    <motion.div
      className="relative z-30 -mx-6 mt-12 max-w-1440 lg:mx-auto lg:mt-[70px]"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2, duration: 1, ease }}
    >
      {imageSource ? (
        <Image
          className={cn('hidden lg:block', className)}
          src={imageSource}
          alt="Dashboard Dark"
          width={1000}
          height={610}
          priority
        />
      ) : null}
      {imageSourceMobile ? (
        <Image
          className={cn('lg:hidden', className)}
          src={imageSourceMobile}
          alt="Dashboard Dark mobile"
          width={828}
          height={505}
          priority
        />
      ) : null}
    </motion.div>
  );
}

export default function NewHero({
  heading,
  subHeading,
  news,
  cta,
  image = '',
  imageMobile = '',
}: {
  heading?: string;
  subHeading?: ReactNode;
  news: { label: string; url: string };
  cta: { label: string; url: string };
  image?: string;
  imageMobile?: string;
}) {
  return (
    <section
      id="hero"
      className="relative mx-auto flex w-full flex-col items-center justify-start overflow-hidden px-6 pt-[137px] lg:px-8 lg:pt-36"
    >
      <HeroPill label={news.label} url={news.url} />
      <HeroTitles title={heading} subTitle={subHeading} />
      <HeroCTA {...cta} />
      <HeroImageDark imageSource={image} imageSourceMobile={imageMobile} />
      <div
        className="pointer-events-none absolute bottom-0 left-0 z-40 h-[30%] w-full lg:h-[16%]"
        style={{
          background:
            'linear-gradient(180deg, rgba(5, 5, 10, 0.00) 0%, #05050A 100%)',
        }}
      ></div>
      <Background />
    </section>
  );
}
